import { store } from "@/lib/states/store";
import { supabase } from "@/lib/supabase/supabaseClient";
import { SupabaseProvider } from "@/lib/supabase/supabaseProvider";
import { usePushNotificationListener } from "@/hooks/use-push";
import { useAppBadgeSync } from "@/hooks/use-app-badge-sync";

import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { OrgSwitchOverlay } from "@/components/OrgSwitchOverlay";
import type { Session } from "@supabase/supabase-js";
import { useRestTimerToast } from "@/lib/hooks/useRestTimerToast";
import { useSectionClocks } from "@/lib/hooks/useSectionClocks";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  useCoach,
  useOrg,
  useSessionInitialised,
  useUserId,
} from "@/lib/states/session/hooks";
import { ensureAuthenticatedSession } from "@/lib/auth/sessionBootstrap";
import type { SessionSubscription } from "@/lib/auth/sessionTypes";
import { trpcVanilla } from "@/lib/trpc/vanilla";
import { getCachedSubscription } from "@/lib/billing/subscriptionCache";

const GlobalUpgradeDialog = lazy(
  () => import("@/components/GlobalUpgradeDialog"),
);
const CoachAppDialog = lazy(() => import("@/components/CoachAppDialog"));
const MfaChallengeScreen = lazy(
  () => import("@/components/auth/MfaChallengeScreen"),
);

const MFA_ENABLED = false;

type AuthRouteContext = {
  session?: {
    user: {
      id: string;
    };
  } | null;
  needsMfa?: boolean;
};

export const Route = createFileRoute("/_auth")({
  component: AuthenticatedLayout,
  beforeLoad: async ({ context }) => {
    if (!context.session) {
      store.getState().session.reset();
      throw redirect({
        to: "/login",
        replace: true,
      });
    }

    if (MFA_ENABLED) {
      const mfa = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (!mfa.error && mfa.data.nextLevel === "aal2" && mfa.data.currentLevel !== "aal2") {
        return { needsMfa: true };
      }
    }

    await ensureAuthenticatedSession(context.session.user, { staleOk: true });

    const resolvedSessionState = store.getState().session;
    const resolvedOrg =
      resolvedSessionState.userId === context.session.user.id
        ? resolvedSessionState.org
        : undefined;

    if (!resolvedOrg?.setupComplete) {
      throw redirect({
        to: "/setup",
        replace: true,
      });
    }
  },
  loader: async ({ context }) => {
    if (!context.session) {
      store.getState().session.reset();
      throw redirect({
        to: "/login",
        replace: true,
      });
    }

    if ((context as AuthRouteContext).needsMfa) {
      return {
        session: context.session,
        subscription: undefined,
        needsMfa: true,
      };
    }

    const orgId = store.getState().session.org?.id ?? null;
    const subscription =
      (orgId ? getCachedSubscription(orgId) : undefined) ??
      store.getState().session.subscription;

    return { session: context.session, subscription, needsMfa: false };
  },
});

function AuthenticatedLayout() {
  const { session, subscription, needsMfa } = Route.useLoaderData();
  const [mfaVerifiedSession, setMfaVerifiedSession] = useState<Session | null>(
    null,
  );

  if (needsMfa && !mfaVerifiedSession) {
    return (
      <Suspense
        fallback={
          <div id="initial-loader">
            <div className="spinner" />
          </div>
        }
      >
        <MfaChallengeScreen
          session={session}
          onVerified={setMfaVerifiedSession}
        />
      </Suspense>
    );
  }

  if (needsMfa && mfaVerifiedSession) {
    return (
      <div id="initial-loader">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <AuthenticatedApp
      session={mfaVerifiedSession ?? session}
      subscription={subscription}
    />
  );
}

function AuthenticatedApp({
  session,
  subscription,
}: {
  session: Session;
  subscription: SessionSubscription | undefined;
}) {
  const coach = useCoach();
  const userId = useUserId();
  const org = useOrg();
  const sessionInitialised = useSessionInitialised();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionInitialised) return;
    if (!userId) {
      window.location.assign("/login");
      return;
    }
    if (!org?.setupComplete) {
      void navigate({ to: "/setup", replace: true });
    }
  }, [navigate, org?.setupComplete, sessionInitialised, userId]);

  useEffect(() => {
    if (!userId) return;
    if (typeof window === "undefined") return;

    const updateTimezone = async () => {
      try {
        const { getTimezone } = await import("countries-and-timezones");
        let detectedTimezone: string | null = null;
        try {
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const resolved = timeZone ? getTimezone(timeZone) : null;
          detectedTimezone = resolved?.name ?? null;
        } catch {
          detectedTimezone = null;
        }

        if (!detectedTimezone) return;
        if (coach?.timezone === detectedTimezone) return;

        await trpcVanilla.auth.updateTimezone.mutate({
          timezone: detectedTimezone,
        });
        const currentCoach = store.getState().session.coach;
        if (currentCoach) {
          store.getState().session.setCoach({
            ...currentCoach,
            timezone: detectedTimezone,
          });
        }
      } catch (error) {
        console.error("Error updating coach timezone", error);
      }
    };

    void updateTimezone();
  }, [coach?.timezone, userId]);

  usePushNotificationListener(session.user.id);
  useAppBadgeSync(session.user.id);
  useRestTimerToast();
  useSectionClocks();

  return (
    <SupabaseProvider session={session} subscription={subscription}>
      <Suspense fallback={null}>
        <GlobalUpgradeDialog />
        <CoachAppDialog />
      </Suspense>
      <OrgSwitchOverlay />
      <Outlet />
    </SupabaseProvider>
  );
}
